import { useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPhone, normalizePhone } from '@monrdv/shared';

/**
 * Softphone.
 *
 * This business runs on the telephone: 3925 calls in the imported history, every one
 * recorded, 40% transcribed. A coordinator is on a call while doing everything else, so the
 * phone belongs in the frame rather than on a page you navigate to.
 *
 * **It is deliberately a simulation.** No carrier is connected and no call is placed. The
 * UI is complete — dial pad, ringing, answered, hold, mute, transfer, duration, recent
 * calls — so that the screens built around it are real, and so that wiring a live provider
 * later is an implementation of `PhoneProvider` rather than a redesign.
 *
 * The banner saying so is permanent and not dismissible. A phone control that looks
 * functional but silently does nothing would be worse than no phone control at all.
 */

type CallState =
  | { status: 'idle' }
  | { status: 'dialing'; number: string; startedAt: number }
  | { status: 'ringing'; number: string; startedAt: number; inbound: boolean; callerName?: string }
  | { status: 'active'; number: string; answeredAt: number; muted: boolean; held: boolean; callerName?: string };

type Action =
  | { type: 'digit'; digit: string }
  | { type: 'backspace' }
  | { type: 'dial' }
  | { type: 'answer' }
  | { type: 'hangup' }
  | { type: 'toggleMute' }
  | { type: 'toggleHold' }
  | { type: 'incoming'; number: string; callerName?: string }
  | { type: 'connected' };

type State = { call: CallState; entry: string; open: boolean };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'digit':
      // A dial pad accepts at most a full international number; beyond that the user is
      // leaning on the keyboard.
      return state.entry.length >= 15 ? state : { ...state, entry: state.entry + action.digit };
    case 'backspace':
      return { ...state, entry: state.entry.slice(0, -1) };
    case 'dial': {
      const number = normalizePhone(state.entry);
      if (!number) return state;
      return { ...state, call: { status: 'dialing', number, startedAt: Date.now() }, open: true };
    }
    case 'connected':
      if (state.call.status !== 'dialing' && state.call.status !== 'ringing') return state;
      return {
        ...state,
        call: {
          status: 'active',
          number: state.call.number,
          answeredAt: Date.now(),
          muted: false,
          held: false,
          callerName: 'callerName' in state.call ? state.call.callerName : undefined,
        },
      };
    case 'incoming':
      return {
        ...state,
        open: true,
        call: { status: 'ringing', number: action.number, startedAt: Date.now(), inbound: true, callerName: action.callerName },
      };
    case 'answer':
      if (state.call.status !== 'ringing') return state;
      return {
        ...state,
        call: { status: 'active', number: state.call.number, answeredAt: Date.now(), muted: false, held: false, callerName: state.call.callerName },
      };
    case 'hangup':
      return { ...state, call: { status: 'idle' }, entry: '' };
    case 'toggleMute':
      return state.call.status === 'active' ? { ...state, call: { ...state.call, muted: !state.call.muted } } : state;
    case 'toggleHold':
      return state.call.status === 'active' ? { ...state, call: { ...state.call, held: !state.call.held } } : state;
    default:
      return state;
  }
}

export function Softphone() {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, { call: { status: 'idle' }, entry: '', open: false });
  const panelRef = useRef<HTMLDivElement>(null);

  // The simulation answers after a moment, so the "active call" UI is reachable without a
  // carrier. Replaced by the provider's own events once one is connected.
  useEffect(() => {
    if (state.call.status !== 'dialing') return;
    const id = window.setTimeout(() => dispatch({ type: 'connected' }), 1800);
    return () => window.clearTimeout(id);
  }, [state.call.status]);

  const active = state.call.status === 'active' ? state.call : null;
  const ringing = state.call.status === 'ringing' ? state.call : null;
  const busy = state.call.status !== 'idle';

  return (
    <div className="phone">
      <button
        type="button"
        className={`phone__toggle${busy ? ' phone__toggle--busy' : ''}`}
        aria-expanded={state.open}
        aria-label={t('calls.dial')}
        onClick={() => dispatch({ type: 'digit', digit: '' })}
      >
        <PhoneGlyph />
        {active && <Duration since={active.answeredAt} />}
        {ringing && <span className="phone__ring">{t('calls.inbound')}</span>}
      </button>

      {(state.open || busy) && (
        <div ref={panelRef} className="phone__panel" role="dialog" aria-label={t('calls.dial')}>
          {/* Permanent, undismissible: this phone does not place calls. */}
          <p className="phone__sim">{t('calls.simulated')}</p>

          {ringing && (
            <div className="phone__incoming">
              <span className="label">{t('calls.inbound')}</span>
              <strong className="phone__number phone">{formatPhone(ringing.number)}</strong>
              {ringing.callerName && <span className="phone__caller">{ringing.callerName}</span>}
              <div className="phone__actions">
                <button type="button" className="btn btn--positive" onClick={() => dispatch({ type: 'answer' })}>
                  Répondre
                </button>
                <button type="button" className="btn btn--danger" onClick={() => dispatch({ type: 'hangup' })}>
                  {t('calls.hangUp')}
                </button>
              </div>
            </div>
          )}

          {active && (
            <div className="phone__active">
              <strong className="phone__number phone">{formatPhone(active.number)}</strong>
              <Duration since={active.answeredAt} className="phone__duration tnum" />
              <div className="phone__actions">
                <button
                  type="button"
                  className={`btn btn--ghost${active.muted ? ' btn--on' : ''}`}
                  aria-pressed={active.muted}
                  onClick={() => dispatch({ type: 'toggleMute' })}
                >
                  {t('calls.mute')}
                </button>
                <button
                  type="button"
                  className={`btn btn--ghost${active.held ? ' btn--on' : ''}`}
                  aria-pressed={active.held}
                  onClick={() => dispatch({ type: 'toggleHold' })}
                >
                  {t('calls.hold')}
                </button>
                <button type="button" className="btn btn--danger" onClick={() => dispatch({ type: 'hangup' })}>
                  {t('calls.hangUp')}
                </button>
              </div>
            </div>
          )}

          {!busy && (
            <>
              <div className="phone__entry">
                <input
                  value={state.entry}
                  onChange={() => undefined}
                  readOnly
                  className="phone__display phone"
                  aria-label={t('calls.dial')}
                  placeholder="000 000-0000"
                />
                <button
                  type="button"
                  className="phone__back"
                  onClick={() => dispatch({ type: 'backspace' })}
                  aria-label="Effacer"
                  disabled={!state.entry}
                >
                  ⌫
                </button>
              </div>

              <div className="phone__pad">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((d) => (
                  <button key={d} type="button" className="phone__key tnum" onClick={() => dispatch({ type: 'digit', digit: d })}>
                    {d}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="btn btn--primary phone__call"
                disabled={!normalizePhone(state.entry)}
                onClick={() => dispatch({ type: 'dial' })}
              >
                {t('calls.dial')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Live call timer. Ticks once a second, which is all a call duration needs. */
function Duration({ since, className }: { since: number; className?: string }) {
  const [, tick] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);
  const seconds = Math.floor((Date.now() - since) / 1000);
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return <span className={className ?? 'tnum'}>{`${mm}:${ss}`}</span>;
}

function PhoneGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M3.5 2.5h2l1 3-1.5 1a7 7 0 0 0 3.5 3.5l1-1.5 3 1v2a1 1 0 0 1-1 1A10.5 10.5 0 0 1 2.5 3.5a1 1 0 0 1 1-1z" />
    </svg>
  );
}
